import React from 'react';
import { motion } from 'framer-motion';
import { Mail, MessageCircle, Clock, Phone, MapPin, ChevronRight, Send } from 'lucide-react';
import { Link } from 'react-router-dom';

const ContactCard = ({ icon: Icon, title, description, action, actionHref, actionText, comingSoon }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="bg-white rounded-[20px] shadow-md p-6 border border-[#E6EFFD] hover:shadow-lg transition-all group relative overflow-hidden"
    >
      {comingSoon && (
        <div className="absolute top-3 right-3 px-3 py-1 bg-gradient-to-r from-[#FFB800] to-[#FF9AA2] text-white text-sm rounded-full font-medium">
          Coming Soon
        </div>
      )}
      <div className="inline-flex items-center justify-center p-3 rounded-full bg-gradient-to-r from-[#5E60CE] to-[#4F86F7] text-white mb-4">
        <Icon className="h-6 w-6" />
      </div>
      <h3 className="text-xl font-bold text-[#5E60CE] mb-2 font-display">{title}</h3>
      <p className="text-gray-600 mb-4">{description}</p>
      {action && (
        <a
          href={actionHref}
          className={`inline-flex items-center ${
            comingSoon 
              ? 'text-gray-400 cursor-not-allowed' 
              : 'text-[#4F86F7] hover:text-[#5E60CE] group-hover:translate-x-1'
          } transition-colors duration-300`}
          onClick={comingSoon ? (e) => e.preventDefault() : undefined}
        >
          {actionText}
          <Send className="h-4 w-4 ml-2" />
        </a>
      )}
    </motion.div>
  );
};

const Contact = () => {
  return (
    <div className="min-h-screen bg-white font-kidBody">
      {/* Hero Section */}
      <div className="bg-gradient-to-b from-[#E3F2FD] to-white pt-16 pb-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-[#5E60CE] mb-6 font-display">Contact Us</h1>
            <p className="text-xl text-[#4F86F7] mb-4 font-display">We're here to help with your magical journey</p>
            <div className="w-20 h-1 bg-gradient-to-r from-[#FF6B6B] to-[#FF9AA2] mx-auto rounded-full"></div>
            <div className="flex items-center justify-center space-x-2 text-sm text-gray-500 font-display mt-6">
              <Link to="/" className="hover:text-[#5E60CE]">Home</Link>
              <ChevronRight className="h-4 w-4" />
              <span>Contact</span>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="max-w-4xl mx-auto">
          {/* Contact Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            <ContactCard
              icon={Mail}
              title="Email Support"
              description="Have questions? Our support team is ready to help you with any inquiries."
              action={true}
              actionHref="mailto:llamatale89@gmail.com"
              actionText="llamatale89@gmail.com"
            />
            <ContactCard
              icon={MessageCircle}
              title="Chat Support"
              description="Live chat support will be available during business hours. Stay tuned for this exciting feature!"
              action={true}
              actionHref="#"
              actionText="Start Chat"
              comingSoon={true}
            />
          </div>

          {/* Support Hours Section */}
          {/* <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-[24px] shadow-lg p-8 sm:p-10 border border-[#E6EFFD] mb-16"
          >
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-[#5E60CE] mb-4 font-display">Support Hours</h2>
              <p className="text-gray-600">We're available to assist you during the following hours:</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="text-center p-6 bg-[#F8FBFF] rounded-xl">
                <h3 className="font-bold text-[#4F86F7] mb-2">Weekdays</h3>
                <p className="text-gray-600">9:00 AM - 6:00 PM EST</p>
              </div>
              <div className="text-center p-6 bg-[#F8FBFF] rounded-xl">
                <h3 className="font-bold text-[#4F86F7] mb-2">Weekends</h3>
                <p className="text-gray-600">10:00 AM - 4:00 PM EST</p>
              </div>
            </div>
          </motion.div> */}

          {/* FAQ Redirect */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-2xl font-bold text-[#5E60CE] mb-4 font-display">Looking for quick answers?</h2>
            <p className="text-gray-600 mb-6">
              Check out our FAQ section for instant answers to common questions
            </p>
            <Link 
              to="/faq"
              className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-[#5E60CE] to-[#4F86F7] text-white rounded-full font-medium hover:shadow-lg transition-shadow"
            >
              Visit FAQ Page
            </Link>
          </motion.div>
        </div>
      </div>

      {/* Decorative bottom */}
      <div className="py-10 bg-[#F8FBFF]">
        <div className="container mx-auto px-4">
          <div className="flex justify-center space-x-8">
            {[
              { icon: Mail, color: "text-[#5E60CE]" },
              { icon: MessageCircle, color: "text-[#FF9AA2]" },
              { icon: Clock, color: "text-[#4F86F7]" },
              { icon: Phone, color: "text-[#FFD54F]" },
              { icon: MapPin, color: "text-[#81C784]" }
            ].map((item, index) => (
              <motion.div
                key={index}
                animate={{ y: [0, -10, 0] }}
                transition={{ 
                  duration: 2,
                  delay: index * 0.5,
                  repeat: Infinity,
                  repeatDelay: 1
                }}
                className="opacity-70"
              >
                <item.icon className={`h-8 w-8 ${item.color}`} />
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact; 